import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card } from 'react-bootstrap';
import { ErrorToast } from '../../../utils/toasters';
import { IModashInfoResponse } from '../dto/IModash';
import { ModashService } from '../ModashService';
import { ModashSkeleton } from './ModashSkeleton';
import { ModashStatusBadge } from './ModashStatusBadge';
import { ModashStatusLegend } from './ModashStatusLegend';

const formatNumber = (value?: number) => (typeof value === 'number' ? value.toLocaleString() : '-');

export function ModashInfo() {
    const [info, setInfo] = useState<IModashInfoResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchInfo = useCallback(() => {
        setLoading(true);
        setError(null);
        ModashService.getModashHealthInfo()
            .then(data => setInfo(data))
            .catch(err => {
                setError('Unable to fetch Modash data.');
                ErrorToast(err);
            })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        fetchInfo();
    }, [fetchInfo]);

    const details = info?.healthData?.details ?? [];
    const billing = info?.userData?.billing;

    return (
        <>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h3 className="text-dark fw-bold fs-2 mb-0">Modash Service</h3>
                <Button variant="outline-primary" size="sm" disabled={loading} onClick={fetchInfo}>
                    Refresh
                </Button>
            </div>

            {loading && <ModashSkeleton rows={details.length || undefined} />}
            {error && <Alert variant="danger">{error}</Alert>}
            {!loading && info && (
                <>
                    {info.userData?.error && (
                        <Alert variant="warning">Modash account data is currently unavailable.</Alert>
                    )}
                    <Card className="mb-4 p-4 mt-3">
                        <div className="row text-center">
                            <div className="col-md-4 mb-3 mb-md-0">
                                <small className="text-muted text-uppercase fw-bold d-block mb-2">Overall status</small>
                                <span className="fs-3">
                                    <ModashStatusBadge status={info.healthData?.status} />
                                </span>
                            </div>
                            <div className="col-md-4 mb-3 mb-md-0">
                                <small className="text-muted text-uppercase fw-bold d-block mb-2">Tokens left</small>
                                <span className="fs-2 fw-bold text-dark">{formatNumber(billing?.credits)}</span>
                            </div>
                            <div className="col-md-4">
                                <small className="text-muted text-uppercase fw-bold d-block mb-2">
                                    Raw requests left
                                </small>
                                <span className="fs-2 fw-bold text-dark">{formatNumber(billing?.rawRequests)}</span>
                            </div>
                        </div>
                    </Card>

                    <Card className="mb-4 p-4">
                        <h5 className="mb-3">Endpoint status</h5>
                        <div className="list-group">
                            {details.map(service => (
                                <div
                                    key={service.endpoint}
                                    className="list-group-item d-flex justify-content-between align-items-center"
                                >
                                    <span>{service.endpoint}</span>
                                    <ModashStatusBadge status={service.status} />
                                </div>
                            ))}
                        </div>
                    </Card>
                </>
            )}

            <ModashStatusLegend />
        </>
    );
}
